import { z } from 'zod';

/**
 * Rattrapage du tour en cours après un rechargement de page (#1888, spec #1909).
 *
 * Le backend tient, pour chaque tour, un « journal d'affichage » : ce que le tour a
 * donné à VOIR depuis son début. Une page qui reprend un tour au chargement le demande
 * (`session_resume.catchup`) et le reçoit en trames `turn_catchup`, avant le direct.
 *
 * WHY une liste FERMÉE : le web n'a aucune déduplication sur les trames qui demandent
 * une action au navigateur (Canvas, cartes de confirmation, actions d'interface).
 * Rejouer l'une d'elles sur une page neuve l'exécuterait une seconde fois. Seules les
 * trames ci-dessous, sans effet de bord, entrent au journal. En ajouter une est une
 * décision explicite, accompagnée d'un test qui montre que la rejouer est sans effet.
 *
 * Chaque entrée garde la forme de sa trame du direct (sans `timestamp`) : le web
 * l'applique avec le même réducteur que le direct, donc le rendu est identique.
 */
export const TurnDisplayEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('agent_thinking'), payload: z.object({ delta: z.string() }) }),
  z.object({ type: z.literal('agent_message_delta'), payload: z.object({ delta: z.string() }) }),
  z.object({ type: z.literal('agent_message'), payload: z.object({ content: z.string() }) }),
  z.object({ type: z.literal('agent_status_note'), payload: z.object({ message: z.string() }) }),
  z.object({
    type: z.literal('backend_tool'),
    payload: z.object({
      tool: z.string(),
      status: z.enum(['preparing', 'started', 'in_progress', 'completed', 'error']),
      data: z.record(z.string(), z.unknown()).optional(),
    }),
  }),
]);
export type TurnDisplayEntry = z.infer<typeof TurnDisplayEntrySchema>;

/**
 * Trame de rattrapage. Le journal part en une ou plusieurs trames bornées en taille,
 * émises sans attente entre elles : aucune trame du direct ne peut s'intercaler.
 */
export const TurnCatchupFrameSchema = z.object({
  type: z.literal('turn_catchup'),
  timestamp: z.string(),
  payload: z.object({
    entries: z.array(TurnDisplayEntrySchema),
    /** Rang de la trame dans ce rattrapage, à partir de 0. */
    part: z.number().int().nonnegative(),
    /** Dernière trame : le web applique alors TOUTES les entrées reçues en une seule mise à jour. */
    last: z.boolean(),
    /**
     * Le journal a dépassé son plafond de taille : le début du tour n'est pas disponible
     * (`entries` est vide). Le tour est adopté quand même ; le web le signale par une note
     * et la réponse complète revient de la base à la fin du tour.
     */
    incomplete: z.boolean(),
  }),
});
export type TurnCatchupFrame = z.infer<typeof TurnCatchupFrameSchema>;
